import React, { useState, useEffect, useMemo } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie
} from "recharts";
import moment from "moment";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function DoctorAppointments({ doctor }) {
    const [appointments, setAppointments] = useState([]);
    const [requests, setRequests] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);

    // Reassign Modal State
    const [showModal, setShowModal] = useState(false);
    const [selectedAppt, setSelectedAppt] = useState(null);
    const [selectedDoctorId, setSelectedDoctorId] = useState("");
    const [sortOrder, setSortOrder] = useState("latest"); // 'latest' or 'old'

    const fetchData = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API}/api/doctor-portal/${doctor.id}/appointments`);
            const data = await res.json();
            
            setAppointments(data.appointments || []);
            setRequests(data.requests || []);

            // Also fetch all doctors for reassignment options
            const docRes = await fetch(`${API}/api/doctors/all`);
            const docData = await docRes.json();
            // Filter out self and only same specialization if you want (or just all active)
            const available = docData.filter(d => d.id !== doctor.id && d.specialization === doctor.specialization && d.is_active === 1);
            setDoctors(available);

            setLoading(false);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load appointments");
            setLoading(false);
        }
    };

    const stats = useMemo(() => {
        const now = moment();
        const todayStr = now.format("YYYY-MM-DD");
        const tomorrowStr = now.clone().add(1, 'day').format("YYYY-MM-DD");

        const totalMeetings = appointments.length;

        // Parse appointment date — MySQL may return it as "2026-04-02T00:00:00.000Z" or "2026-04-02"
        const parseApptDate = (a) => {
            const rawDate = a.date ? a.date.toString().slice(0, 10) : '';
            return rawDate;
        };

        const upcoming = appointments.filter(a => {
            if (a.status === 'cancelled') return false;
            const rawDate = parseApptDate(a);
            const apptMoment = moment(`${rawDate} ${a.time}`, "YYYY-MM-DD HH:mm:ss");
            return apptMoment.isAfter(now);
        }).length;

        const cancelled = appointments.filter(a => a.status === 'cancelled').length;
        const todayCount = appointments.filter(a => parseApptDate(a) === todayStr).length;
        const tomorrowCount = appointments.filter(a => parseApptDate(a) === tomorrowStr).length;
        const totalPatients = new Set(appointments.map(a => a.user_id)).size;

        return {
            totalMeetings,
            upcoming,
            cancelled,
            todayCount,
            tomorrowCount,
            totalPatients
        };
    }, [appointments]);

    const chartData = useMemo(() => [
        { name: 'Upcoming', value: stats.upcoming, color: '#0ea5e9' },
        { name: 'Cancelled', value: stats.cancelled, color: '#ef4444' },
        { name: 'Today', value: stats.todayCount, color: '#10b981' },
        { name: 'Tomorrow', value: stats.tomorrowCount, color: '#f59e0b' },
    ], [stats]);

    // Sort by BOOKING DATE (created_at) — "Latest" = most recently booked first
    const sortedAppointments = useMemo(() => {
        return [...appointments].sort((a, b) => {
            // created_at from MySQL may be ISO string or Date object
            const createdA = moment(a.created_at);
            const createdB = moment(b.created_at);
            return sortOrder === "latest" ? createdB - createdA : createdA - createdB;
        });
    }, [appointments, sortOrder]);

    // Helper to format a MySQL date value like "2026-04-01T00:00:00.000Z" -> "01 Apr 2026"
    const formatDate = (rawDate) => {
        if (!rawDate) return 'N/A';
        return moment(rawDate.toString().slice(0, 10), "YYYY-MM-DD").format("DD MMM YYYY");
    };

    // Helper to get booking time ago label
    const bookingAgo = (created_at) => {
        if (!created_at) return '';
        return moment(created_at).fromNow();
    };

    useEffect(() => {
        if (doctor?.id) fetchData();
    }, [doctor]);

    // Handle incoming reassignment
    const handleAction = async (apptId, action) => {
        try {
            const res = await fetch(`${API}/api/doctor-portal/appointments/${apptId}/reassign/confirm`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ action, doctorId: doctor.id })
            });

            if (res.ok) {
                toast.success(`Request ${action}`);
                fetchData();
            } else {
                toast.error("Action failed");
            }
        } catch (err) {
            toast.error("Error processing request");
        }
    };

    // Request reassignment
    const requestReassign = async () => {
        if (!selectedDoctorId) return toast.error("Please select a doctor to reassign to");

        try {
            const res = await fetch(`${API}/api/doctor-portal/appointments/${selectedAppt.id}/reassign`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ new_doctor_id: selectedDoctorId, doctorId: doctor.id })
            });
            const data = await res.json();
            if (res.ok) {
                toast.success("Reassignment requested!");
                setShowModal(false);
                fetchData();
            } else {
                toast.error(data.message || "Failed to reassign");
            }
        } catch(err) {
            toast.error("Server error");
        }
    };


    return (
        <div className="max-w-6xl mx-auto px-4 md:px-8 space-y-8 relative z-10 w-full pb-20">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-[#0c4a6e] font-outfit tracking-tight">Doctor Dashboard</h1>
                    <p className="text-slate-500 text-sm mt-1">Monitor your appointments and patient statistics in real-time.</p>
                </div>
                <div className="flex bg-white/80 backdrop-blur-md p-1 rounded-xl border border-slate-200 shadow-sm self-start">
                    <button 
                        onClick={() => setSortOrder("latest")}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${sortOrder === 'latest' ? 'bg-[#0284c7] text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}
                    >
                        Latest
                    </button>
                    <button 
                        onClick={() => setSortOrder("old")}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${sortOrder === 'old' ? 'bg-[#0284c7] text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}
                    >
                        Oldest
                    </button>
                </div>
            </div>

            {/* Statistics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                    { label: "Total Meetings", value: stats.totalMeetings, icon: "📊", color: "from-blue-500 to-sky-400" },
                    { label: "Upcoming", value: stats.upcoming, icon: "📅", color: "from-emerald-500 to-teal-400" },
                    { label: "Today", value: stats.todayCount, icon: "🎯", color: "from-amber-500 to-orange-400" },
                    { label: "Total Patients", value: stats.totalPatients, icon: "👥", color: "from-indigo-500 to-violet-400" },
                ].map((item, i) => (
                    <div key={i} className="bg-white/70 backdrop-blur-md border border-white/60 p-6 rounded-2xl shadow-lg group hover:translate-y-[-5px] transition-all">
                        <div className="flex justify-between items-start mb-4">
                            <span className="text-2xl">{item.icon}</span>
                            <span className={`px-2 py-1 rounded-lg bg-gradient-to-br ${item.color} text-white text-xs font-bold shadow-sm`}>
                                LIVE
                            </span>
                        </div>
                        <h3 className="text-3xl font-extrabold text-slate-800 mb-1">{item.value}</h3>
                        <p className="text-slate-500 text-sm font-medium">{item.label}</p>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                 {/* Visual Representation (Chart) */}
                 <div className="lg:col-span-1 bg-white/70 backdrop-blur-md rounded-2xl p-6 shadow-xl border border-white/60 flex flex-col items-center">
                    <h2 className="text-lg font-bold text-slate-800 mb-6 self-start">Meeting Distribution</h2>
                    <div className="w-full h-[240px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={chartData}
                                    innerRadius={60}
                                    outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip 
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-4 w-full">
                        {chartData.map((d, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full" style={{ background: d.color }}></span>
                                <span className="text-[12px] text-slate-600 font-medium">{d.name} ({d.value})</span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-8 pt-6 border-t border-slate-100 w-full">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-slate-500">Tomorrow's Load</span>
                            <span className="text-sm font-bold text-amber-500">{stats.tomorrowCount} meetings</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2">
                            <div 
                                className="bg-amber-400 h-2 rounded-full transition-all duration-1000" 
                                style={{ width: `${Math.min((stats.tomorrowCount / (stats.totalMeetings || 1)) * 100, 100)}%` }}
                            ></div>
                        </div>
                    </div>
                </div>

                {/* Secondary Stats/Small List */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Notification Bar for Incoming Requests */}
                    {requests.length > 0 && (
                        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-400 p-5 rounded-2xl shadow-lg animate-pulse">
                            <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                                <span className="text-amber-500">🔔</span> Pending Transfers ({requests.length})
                            </h2>
                            <div className="space-y-3">
                                {requests.map(req => (
                                    <div key={req.id} className="flex flex-wrap md:flex-nowrap items-center justify-between bg-white/60 p-4 rounded-xl border border-amber-100 shadow-sm">
                                        <div>
                                            <p className="text-[14px] text-slate-700 font-bold">Dr. {req.requested_by_name} <span className="text-slate-500 font-normal">→ Me</span></p>
                                            <p className="text-[13px] text-slate-600 mt-0.5">Patient: <b>{req.patient_name}</b></p>
                                        </div>
                                        <div className="flex gap-2 mt-3 md:mt-0">
                                            <button onClick={() => handleAction(req.id, "accepted")} className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-1.5 rounded-lg text-[13px] font-bold transition-all shadow-sm">Accept</button>
                                            <button onClick={() => handleAction(req.id, "declined")} className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-500 px-4 py-1.5 rounded-lg text-[13px] font-bold transition-all">Ignore</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Cancelled Alert Box (If any) */}
                    {stats.cancelled > 0 && (
                        <div className="bg-rose-50 border-l-4 border-rose-400 p-4 rounded-xl flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="text-xl">⚠️</span>
                                <span className="text-sm font-semibold text-rose-700">{stats.cancelled} appointments have been cancelled recently.</span>
                            </div>
                        </div>
                    )}

                    {/* Main Appointments Table/List Card */}
                    <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 shadow-xl border border-white/60">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-slate-800 font-outfit">Appointments Queue</h2>
                            <span className="text-[12px] font-bold bg-sky-100 text-sky-700 px-3 py-1 rounded-full">{sortedAppointments.length} Active</span>
                        </div>
                        
                        {loading ? (
                            <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>
                        ) : sortedAppointments.length === 0 ? (
                            <div className="text-center py-12">
                                <div className="text-4xl mb-4">🍃</div>
                                <p className="text-slate-400 font-medium">No appointments found in the queue.</p>
                            </div>
                        ) : (
                            <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                                {sortedAppointments.map(appt => (
                                    <div key={appt.id} className={`bg-white rounded-2xl p-4 border border-slate-100 shadow-sm hover:shadow-md transition-all flex flex-col md:flex-row justify-between items-start md:items-center gap-4 group ${appt.status === 'cancelled' ? 'opacity-60 grayscale-[0.5]' : ''}`}>
                                        <div className="flex gap-4 items-center">
                                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-400 to-blue-500 text-white flex items-center justify-center font-bold text-lg shadow-inner">
                                                {appt.patient_name?.charAt(0) || 'P'}
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-bold text-[15px] text-slate-800">{appt.patient_name}</h3>
                                                    {appt.status === 'cancelled' && <span className="text-[10px] bg-rose-100 text-rose-600 px-2 py-0.5 rounded-md font-bold uppercase">Cancelled</span>}
                                                </div>
                                                <div className="text-[13px] text-slate-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                                                    <span className="flex items-center gap-1.5 font-medium"><span className="text-sky-500">📅</span> {formatDate(appt.date)}</span>
                                                    <span className="flex items-center gap-1.5 font-medium"><span className="text-sky-500">🕒</span> {appt.time?.slice(0,5)}</span>
                                                    <span className={`flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full capitalize ${appt.appointment_type === 'online' ? 'bg-indigo-50 text-indigo-600' : 'bg-emerald-50 text-emerald-600'}`}>
                                                        {appt.appointment_type === 'online' ? '🌐 Online' : '🏥 Clinic'}
                                                    </span>
                                                    {appt.created_at && (
                                                        <span className="text-[11px] text-slate-400 italic">Booked {bookingAgo(appt.created_at)}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="flex flex-wrap md:flex-nowrap w-full md:w-auto gap-3 justify-end items-center">
                                            {appt.reassignment_status === 'pending' && appt.reassigned_to && (
                                                <span className="text-[11px] bg-amber-50 text-amber-600 border border-amber-200 px-3 py-1.5 rounded-lg font-bold">
                                                    Transfer Out Pending
                                                </span>
                                            )}

                                            {appt.status !== 'cancelled' && appt.reassignment_status !== 'pending' && (
                                                <>
                                                    <button 
                                                       onClick={() => { setSelectedAppt(appt); setShowModal(true); }}
                                                       className="text-[12px] font-bold text-slate-500 hover:text-sky-600 bg-slate-50 hover:bg-sky-50 px-4 py-2 rounded-xl border border-slate-200 transition-all"
                                                    >
                                                        Reassign
                                                    </button>
                                                    
                                                    {appt.meeting_link && appt.appointment_type === 'online' && (
                                                        <a 
                                                            href={appt.meeting_link}
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="bg-[#0284c7] hover:bg-[#0369a1] text-white font-bold text-[12px] px-5 py-2 rounded-xl shadow-lg shadow-sky-200 hover:-translate-y-0.5 transition-all flex items-center gap-2"
                                                        >
                                                            Join Call
                                                        </a>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Reassign Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
                        <h2 className="text-xl font-bold text-slate-800 mb-2">Reassign Appointment</h2>
                        <p className="text-sm text-slate-500 mb-5">
                            Transferring <b>{selectedAppt?.patient_name}</b> ({formatDate(selectedAppt?.date)} at {selectedAppt?.time?.slice(0,5)}) to another doctor in your department.
                        </p>
                        
                        <div className="mb-6">
                            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                                Select Available Doctor
                            </label>
                            {doctors.length === 0 ? (
                                <div className="text-sm text-rose-500 bg-rose-50 p-3 rounded-lg border border-rose-100">
                                    No other active doctors found in your specialization.
                                </div>
                            ) : (
                                <select 
                                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 bg-slate-50 focus:bg-white focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 outline-none transition-all"
                                    value={selectedDoctorId}
                                    onChange={(e) => setSelectedDoctorId(e.target.value)}
                                >
                                    <option value="">Select a peer doctor...</option>
                                    {doctors.map(d => (
                                        <option key={d.id} value={d.id}>Dr. {d.name}</option>
                                    ))}
                                </select>
                            )}
                        </div>

                        <div className="flex gap-3 justify-end">
                            <button 
                                onClick={() => { setShowModal(false); setSelectedDoctorId(""); }}
                                className="px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={requestReassign}
                                disabled={!selectedDoctorId}
                                className="px-5 py-2.5 text-sm font-medium text-white bg-sky-500 hover:bg-sky-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl shadow-md transition-all"
                            >
                                Send Request
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            <ToastContainer />
        </div>
    );
}
